import React from "react";
import { formatIDR, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";
import { FileSpreadsheet, FileText, PackagePlus, TruckIcon, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLE = {
  stok_awal: { cls: "bg-sky-100 text-sky-700 hover:bg-sky-100", Icon: PackagePlus },
  pengiriman: { cls: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", Icon: TruckIcon },
  penjualan: { cls: "bg-amber-100 text-amber-700 hover:bg-amber-100", Icon: ShoppingCart },
};

export const StockHistoryTable = ({ data, storeName, testPrefix = "stock-history" }) => {
  const movements = data?.movements || [];
  const summary = data?.summary || {};

  const tf = useTableFilter(movements, {
    searchKeys: ["product_name", "type_label", "notes", "date"],
    defaultSort: { key: "date", dir: "desc" },
  });

  const exportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = tf.rows.map((m, i) => ({
        No: i + 1,
        Tanggal: formatDate(m.date),
        Jenis: m.type_label,
        Produk: m.product_name,
        Masuk: m.masuk || 0,
        Keluar: m.keluar || 0,
        "Saldo Produk": m.saldo,
        "Harga Satuan": m.unit_price || 0,
        Nilai: m.nilai || 0,
        Catatan: m.notes || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 4 }, { wch: 14 }, { wch: 22 }, { wch: 34 }, { wch: 9 }, { wch: 9 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 30 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Riwayat Stok");
      const safe = (storeName || "toko").replace(/[^a-zA-Z0-9]+/g, "_");
      XLSX.writeFile(wb, `Riwayat_Stok_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Excel diunduh (${rows.length} baris)`);
    } catch (e) { toast.error("Gagal ekspor Excel: " + (e.message || e)); }
  };

  const exportPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      await import("jspdf-autotable").catch(() => {});
      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text(`Riwayat Pergerakan Stok — ${storeName || "-"}`, 14, 15);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(
        `${tf.rows.length} pergerakan · Masuk ${summary.total_masuk || 0} · Keluar ${summary.total_keluar || 0} · Saldo Akhir ${summary.saldo_akhir || 0} · Dicetak ${new Date().toLocaleDateString("id-ID")}`,
        14, 22,
      );
      if (doc.autoTable) {
        doc.autoTable({
          head: [["No", "Tanggal", "Jenis", "Produk", "Masuk", "Keluar", "Saldo", "Nilai"]],
          body: tf.rows.map((m, i) => [
            i + 1,
            formatDate(m.date),
            m.type_label,
            m.product_name,
            m.masuk || 0,
            m.keluar || 0,
            m.saldo,
            formatIDR(m.nilai || 0),
          ]),
          startY: 28,
          styles: { fontSize: 8, cellPadding: 1.8 },
          headStyles: { fillColor: [4, 120, 87], textColor: 255 },
          columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
        });
      }
      const safe = (storeName || "toko").replace(/[^a-zA-Z0-9]+/g, "_");
      doc.save(`Riwayat_Stok_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF diunduh");
    } catch (e) { toast.error("Gagal ekspor PDF: " + (e.message || e)); }
  };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-wrap`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Total Masuk</div>
          <div className="font-mono-num text-xl font-bold text-emerald-700">{summary.total_masuk || 0}</div>
        </Card>
        <Card className="p-3 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Total Keluar (Terjual)</div>
          <div className="font-mono-num text-xl font-bold text-amber-700">{summary.total_keluar || 0}</div>
        </Card>
        <Card className="p-3 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Saldo Akhir</div>
          <div className="font-mono-num text-xl font-bold text-[var(--hotto-primary)]">{summary.saldo_akhir || 0}</div>
        </Card>
        <Card className="p-3 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Jumlah Pergerakan</div>
          <div className="font-mono-num text-xl font-bold">{summary.movement_count || 0}</div>
        </Card>
      </div>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b flex flex-wrap items-center gap-2 justify-between">
          <TableToolbar tf={tf} placeholder="Cari produk / jenis / catatan..." />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportExcel} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid={`${testPrefix}-export-excel`}>
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF} className="border-rose-400 text-rose-700 hover:bg-rose-50" data-testid={`${testPrefix}-export-pdf`}>
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <SortHeader tf={tf} col="date">Tanggal</SortHeader>
              <SortHeader tf={tf} col="type_label">Jenis</SortHeader>
              <SortHeader tf={tf} col="product_name">Produk</SortHeader>
              <SortHeader tf={tf} col="masuk" className="text-right">Masuk</SortHeader>
              <SortHeader tf={tf} col="keluar" className="text-right">Keluar</SortHeader>
              <SortHeader tf={tf} col="saldo" className="text-right">Saldo</SortHeader>
            </TableRow></TableHeader>
            <TableBody>
              {tf.rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada pergerakan stok.</TableCell></TableRow>
              )}
              {tf.rows.map((m) => {
                const style = TYPE_STYLE[m.type] || TYPE_STYLE.pengiriman;
                const Icon = style.Icon;
                return (
                  <TableRow key={`${m.type}-${m.ref_id || "op"}-${m.product_id}-${m.seq}`} className="table-row-hover">
                    <TableCell className="font-mono-num text-sm whitespace-nowrap">{formatDate(m.date)}</TableCell>
                    <TableCell>
                      <Badge className={`${style.cls} text-[10px] whitespace-nowrap`}><Icon className="w-3 h-3 mr-1" />{m.type_label}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{m.product_name}</TableCell>
                    <TableCell className="text-right font-mono-num text-emerald-700">{m.masuk ? `+${m.masuk}` : "-"}</TableCell>
                    <TableCell className="text-right font-mono-num text-amber-700">{m.keluar ? `-${m.keluar}` : "-"}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold text-[var(--hotto-primary)]">{m.saldo}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default StockHistoryTable;
